import { MEDIA_ITEM_MIN_DURATION, TIMELINE_RULER_SLICE_WIDTH } from 'consts'
import { IMediaItem, MediaStatus } from 'types/media'
import { IAudioItem } from 'types/audio'
import { ITimelineItem } from 'types/timeline'
import { parseFloatAndFix, isBlobUrl } from 'utils'
import { PROJECT_AUDIO_TYPES } from 'consts/audio'

export const getPixelsPerSecond = (
  timelineRulerSliceDuration: number
): number => TIMELINE_RULER_SLICE_WIDTH / timelineRulerSliceDuration

export const secondsToPixels = (
  seconds: number,
  timelineRulerSliceDuration: number
): number =>
  parseFloatAndFix(seconds * getPixelsPerSecond(timelineRulerSliceDuration), 3)

export const pixelsToSeconds = (
  pixels: number,
  timelineRulerSliceDuration: number
): number =>
  parseFloatAndFix(pixels / getPixelsPerSecond(timelineRulerSliceDuration), 3)

export const convertMediaItemsToTimelineItems = (
  mediaItems: IMediaItem[]
): ITimelineItem[] => {
  return mediaItems.map(mediaItem => ({
    id: mediaItem.id,
    type: mediaItem.type,
    lineIndex: mediaItem.zIndex,
    startTime: mediaItem.startTime,
    duration: mediaItem.duration,
    trimStart: mediaItem.meta.trimStart,
    originalDuration: mediaItem.meta.originalDuration
  }))
}

export const convertAudioItemsToTimelineItems = (
  audioItems: IAudioItem[]
): ITimelineItem[] => {
  return audioItems.map(audioItem => ({
    id: audioItem.id,
    type: audioItem.type,
    lineIndex: audioItem.lineIndex,
    startTime: audioItem.projectStart,
    duration: audioItem.duration,
    trimStart: audioItem.mediaStart,
    originalDuration: audioItem.mediaDuration
  }))
}

export const convertTimelineItemToMediaItem = (
  timelineItem: ITimelineItem,
  mediaItem: IMediaItem
): IMediaItem => {
  return {
    ...mediaItem,
    zIndex: timelineItem.lineIndex,
    startTime: timelineItem.startTime,
    duration: timelineItem.duration,
    meta: {
      ...mediaItem?.meta,
      trimStart: timelineItem.trimStart,
      originalDuration: timelineItem.originalDuration
    }
  }
}

export const convertTimelineItemsToMediaItems = (
  timelineItems: ITimelineItem[],
  mediaItems: IMediaItem[]
): IMediaItem[] => {
  return mediaItems.map(mediaItem => {
    const timelineItem = timelineItems.find(
      item => item.id === mediaItem.id
    ) as ITimelineItem

    return timelineItem
      ? convertTimelineItemToMediaItem(timelineItem, mediaItem)
      : mediaItem
  })
}

export const convertTimelineItemsToAudioItems = (
  timelineItems: ITimelineItem[],
  audioItems: IAudioItem[]
): IAudioItem[] => {
  return audioItems.map(audioItem => {
    const timelineItem = timelineItems.find(
      item => item.id === audioItem.id
    ) as ITimelineItem

    return timelineItem
      ? {
          ...audioItem,
          lineIndex: timelineItem.lineIndex,
          projectStart: timelineItem.startTime,
          duration: timelineItem.duration,
          mediaStart: timelineItem.trimStart as number,
          mediaDuration: timelineItem.originalDuration as number
        }
      : audioItem
  })
}

interface IIsPlayheadOverItemParams {
  playheadTime: number
  itemStartTime: number
  itemDuration: number
}

export const isPlayheadOverItem = ({
  playheadTime,
  itemStartTime,
  itemDuration
}: IIsPlayheadOverItemParams): boolean => {
  return (
    itemStartTime <= playheadTime && playheadTime < itemStartTime + itemDuration
  )
}

export const isItemAvailableToSplit = ({
  playheadTime,
  itemStartTime,
  itemDuration
}: IIsPlayheadOverItemParams) => {
  return (
    playheadTime - itemStartTime >= MEDIA_ITEM_MIN_DURATION &&
    itemStartTime + itemDuration - playheadTime >= MEDIA_ITEM_MIN_DURATION
  )
}

export const canCopyMediaItem = (
  mediaItem: IMediaItem | null | undefined
): boolean =>
  !!(
    mediaItem?.id &&
    !isBlobUrl(mediaItem.meta.path) &&
    mediaItem.meta.status !== MediaStatus.PENDING
  )

export const canCopyAudioItem = (
  audioItem: IAudioItem | null | undefined
): boolean =>
  !!(
    audioItem?.id &&
    !isBlobUrl(audioItem.mediaPath) &&
    audioItem.type !== PROJECT_AUDIO_TYPES.TTS
  )
